import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { Paginator } from 'primereact/paginator';
import { isSuperAdmin } from '../../../utils/adminConfig';

const HourReductionDeclarationListing = ({
    data,
    onEdit,
    onDelete,
    onRequestDelete,
    onApproveDelete,
    onCancelDeleteRequest,
    isLoading,
    onApprove,
    onRejectClick,
    onCancelApprove,
    onRequestEdit,
    onViewDetail,
    onBulkApprove,
    onBulkCancelApprove,
    onBulkDelete,
    searchQuery,
    filters,
    currentUserEmail = ''
}) => {
    const menu = useRef(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    const [first, setFirst] = useState(0);
    const rows = 20;

    const containerRef = useRef(null);
    const scrollLeftRef = useRef(0);

    const handleScroll = (e) => {
        scrollLeftRef.current = e.target.scrollLeft;
    };

    useLayoutEffect(() => {
        if (containerRef.current && scrollLeftRef.current > 0) {
            containerRef.current.scrollLeft = scrollLeftRef.current;
        }
    }, [data, first]);

    useEffect(() => {
        setFirst(0);
        setSelectedIds([]);
    }, [filters, searchQuery]);

    useEffect(() => {
        setSelectedIds(prev => prev.filter(id => data.some(d => d.Id === id)));

        if (data.length > 0 && first >= data.length) {
            setFirst(Math.max(0, Math.floor((data.length - 1) / rows) * rows));
        } else if (data.length === 0) {
            setFirst(0);
        }
    }, [data, first, rows]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(data.map(item => item.Id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (e, id) => {
        if (e.target.checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(itemId => itemId !== id));
        }
    };

    const paginatedData = useMemo(() => data.slice(first, first + rows), [data, first, rows]);

    const onPageChange = (event) => {
        setFirst(event.first);
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr.toString().trim() === "" || dateStr === "0001-01-01T00:00:00") return '—';
        try {
            const timestamp = Date.parse(dateStr);
            if (isNaN(timestamp)) return dateStr;
            return new Date(dateStr).toLocaleDateString('vi-VN');
        } catch (e) {
            return '—';
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Đã duyệt': return { backgroundColor: '#27ae60', color: '#fff' };
            case 'Đã gửi duyệt': return { backgroundColor: '#3498db', color: '#fff' };
            case 'Từ chối': return { backgroundColor: '#e74c3c', color: '#fff' };
            case 'Yêu cầu chỉnh sửa': return { backgroundColor: '#9b59b6', color: '#fff' };
            case 'Đang chỉnh sửa':
            case 'Lưu nháp': return { backgroundColor: '#f39c12', color: '#fff' };
            case 'Không yêu cầu duyệt': return { backgroundColor: '#07b491', color: '#fff' };
            default: return { backgroundColor: '#95a5a6', color: '#fff' };
        }
    };

    const itemTemplate = (item, options) => {
        return (
            <div className={options.className} onClick={(e) => {
                if (e.originalEvent) e.originalEvent.preventDefault();
                options.onClick(e);
            }}
                style={{
                    display: 'flex', alignItems: 'center', padding: '10px 15px',
                    pointerEvents: item.disabled ? 'none' : 'auto',
                    opacity: item.disabled ? 0.5 : 1,
                    transition: 'background-color 0.2s',
                    cursor: item.disabled ? 'default' : 'pointer'
                }}>
                <i className={item.icon} style={{ color: item.color, width: '24px', fontSize: '1.1rem', marginRight: '8px' }}></i>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#495057' }}>{item.label}</span>
            </div>
        );
    };

    const getMenuItems = () => {
        if (!selectedItem) return [];

        const isApproved = selectedItem.Status === 'Đã duyệt' || selectedItem.Status === 'Không yêu cầu duyệt';
        const items = [];

        items.push({
            label: 'Xem chi tiết',
            icon: 'fa-solid fa-eye',
            color: '#3498db',
            command: () => onViewDetail && onViewDetail(selectedItem),
            template: itemTemplate
        });
        items.push({ separator: true });

        if (!isApproved) {
            items.push({ label: 'Phê duyệt', icon: 'fa-solid fa-check-circle', color: '#27ae60', command: () => onApprove(selectedItem.Id), template: itemTemplate });
            items.push({ label: 'Từ chối', icon: 'fa-solid fa-times-circle', color: '#e74c3c', command: () => onRejectClick(selectedItem.Id), template: itemTemplate });
            items.push({ label: 'Yêu cầu chỉnh sửa', icon: 'fa-solid fa-envelope-open-text', color: '#9b59b6', command: () => onRequestEdit(selectedItem), template: itemTemplate });
        } else {
            items.push({ label: 'Hủy duyệt', icon: 'fa-solid fa-undo', color: '#f39c12', command: () => onCancelApprove(selectedItem.Id), template: itemTemplate });
        }

        items.push({ separator: true });

        items.push({
            label: 'Chỉnh sửa',
            icon: 'fa-solid fa-pen-to-square',
            color: '#3498db',
            disabled: isApproved || selectedItem.IsPendingDeletion,
            command: () => onEdit(selectedItem),
            template: itemTemplate
        });

        if (!isApproved) {
            if (selectedItem.IsPendingDeletion) {
                items.push({
                    label: 'Hủy yêu cầu Xóa',
                    icon: 'fa-solid fa-ban',
                    color: '#7f8c8d',
                    command: () => onCancelDeleteRequest && onCancelDeleteRequest(selectedItem.Id),
                    template: itemTemplate
                });

                if (isSuperAdmin(currentUserEmail)) {
                    items.push({
                        label: 'Duyệt XÓA vĩnh viễn',
                        icon: 'fa-solid fa-triangle-exclamation',
                        color: '#c0392b',
                        command: () => onApproveDelete && onApproveDelete(selectedItem.Id),
                        template: itemTemplate
                    });
                }
            } else {
                items.push({
                    label: 'Yêu cầu Xóa',
                    icon: 'fa-solid fa-trash-can',
                    color: '#e74c3c',
                    command: () => onRequestDelete ? onRequestDelete(selectedItem.Id) : onDelete(selectedItem.Id),
                    template: itemTemplate
                });
            }
        }

        return items;
    };

    return (
        <>
            {selectedIds.length > 0 && (
                <div style={{
                    padding: '12px 16px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#e0f2fe', padding: '8px', borderRadius: '50%', display: 'flex', width: '36px', height: '36px', justifyContent: 'center', alignItems: 'center' }}>
                            <i className="fa-solid fa-list-check" style={{ color: '#0284c7', fontSize: '16px' }}></i>
                        </div>
                        <span style={{ fontSize: '15px', color: '#475569' }}>
                            Đã chọn <strong style={{ color: '#0f172a', fontSize: '18px', margin: '0 4px' }}>{selectedIds.length}</strong> mục
                        </span>
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <button
                            onClick={() => onBulkApprove && onBulkApprove(selectedIds)}
                            style={{
                                backgroundColor: '#10b981', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#059669'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#10b981'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-check-circle" style={{ fontSize: '12px' }}></i> Phê duyệt
                        </button>

                        <button
                            onClick={() => onBulkCancelApprove && onBulkCancelApprove(selectedIds)}
                            style={{
                                backgroundColor: '#f59e0b', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f59e0b'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-undo" style={{ fontSize: '12px' }}></i> Hủy duyệt
                        </button>

                        <button
                            onClick={() => onBulkDelete && onBulkDelete(selectedIds)}
                            style={{
                                backgroundColor: '#ef4444', color: 'white', border: 'none',
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '500', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="fa-solid fa-trash-can" style={{ fontSize: '12px' }}></i> Xóa
                        </button>
                    </div>
                </div>
            )}

            <div
                className="table-card"
                ref={containerRef}
                onScroll={handleScroll}
                style={{
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    paddingBottom: '10px',
                    position: 'relative'
                }}
            >
                {isLoading && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 10, display: 'flex', justifyContent: 'center', paddingTop: '60px'
                    }}>
                        <i className="fa-solid fa-circle-notch fa-spin fa-2x" style={{ color: '#3498db', marginBottom: '10px' }}></i>
                    </div>
                )}

                {data.length === 0 && !isLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '60px', color: '#5dade2', marginBottom: '15px' }}></i>
                        <h3 style={{ color: '#e74c3c', margin: '0 0 10px 0' }}>Chưa có dữ liệu kê khai</h3>
                    </div>
                ) : (
                    <>
                        <table className="custom-table" style={{ minWidth: '1700px' }}>
                            <thead>
                                <tr>
                                    <th width="3%" style={{ textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                            checked={data.length > 0 && selectedIds.length === data.length}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th width="3%" style={{ textAlign: 'center' }}>STT</th>
                                    <th width="12%">NGƯỜI DÙNG</th>
                                    <th width="10%">EMAIL</th>
                                    <th width="10%">ĐƠN VỊ</th>
                                    <th width="19%">KHOẢN GIẢM TRỪ</th>
                                    <th width="7%" style={{ textAlign: 'center' }}>TỶ LỆ (%)</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>TỪ NGÀY</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>ĐẾN NGÀY</th>
                                    <th width="4%" style={{ textAlign: 'center' }}>HIỆN TẠI</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>CẬP NHẬT</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>GHI CHÚ</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>NOTE</th>
                                    <th width="10%">NGƯỜI DUYỆT</th>
                                    <th width="8%" style={{ textAlign: 'center' }}>TRẠNG THÁI</th>
                                    <th width="5%" style={{ textAlign: 'center' }}>CHỨC NĂNG</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.map((item, index) => {
                                    const actualIndex = first + index + 1;
                                    const currentStatus = item.Status || 'Đang chỉnh sửa';

                                    const hasNotes = item.Notes && item.Notes.trim() !== "";
                                    const hasNote = item.Note && item.Note.trim() !== "";

                                    return (
                                        <tr key={item.Id} style={{ backgroundColor: selectedIds.includes(item.Id) ? '#f4f9ff' : 'transparent' }}>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                    checked={selectedIds.includes(item.Id)}
                                                    onChange={(e) => handleSelectRow(e, item.Id)}
                                                />
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{actualIndex}</td>
                                            <td>
                                                <div
                                                    style={{
                                                        fontWeight: 'bold',
                                                        color: '#0056b3',
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                    onClick={() => {
                                                        if (item.Status === 'Đã duyệt') {
                                                            onViewDetail && onViewDetail(item);
                                                        } else {
                                                            onEdit && onEdit(item);
                                                        }
                                                    }}
                                                >
                                                    {item.FullName}
                                                </div>
                                                {item.IsPendingDeletion && (
                                                    <div style={{ fontSize: '11px', color: '#e74c3c', fontWeight: 'bold', marginTop: '4px', marginBottom: '4px' }}>
                                                        <i className="fa-solid fa-clock"></i> Đang chờ duyệt xóa
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{item.Email}</td>
                                            <td>{item.DepartmentName}</td>
                                            <td style={{ color: '#2c3e50', fontWeight: '500' }}>{item.ReductionContent || item.ResearchReductionName}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#e67e22' }}>{item.ActualPercentage}%</td>

                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{formatDate(item.StartDate)}</td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{formatDate(item.EndDate)}</td>

                                            <td style={{ textAlign: 'center' }}>
                                                {item.IsCurrent ? <i className="fa-solid fa-circle-check" style={{ color: '#2ecc71', fontSize: '17px' }}></i> : ''}
                                            </td>

                                            <td>
                                                {item.ApproverEmail ? (
                                                    <>
                                                        <div style={{ fontSize: '12px', fontWeight: '500', color: '#333' }}>{item.ApproverName}</div>
                                                        <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{item.ApproverEmail}</div>
                                                    </>
                                                ) : (
                                                    <span style={{ color: '#adb5bd', fontSize: '12px', fontStyle: 'italic' }}>Chưa có</span>
                                                )}
                                            </td>

                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.UpdatedDate || item.UpdatedAt || item.updatedDate || item.CreatedDate || item.CreatedAt)}
                                            </td>

                                            <td style={{
                                                textAlign: hasNotes ? 'left' : 'center',
                                                fontSize: '13px',
                                                wordBreak: 'break-word',
                                                color: '#555'
                                            }}>
                                                {hasNotes ? item.Notes : '—'}
                                            </td>

                                            <td style={{
                                                textAlign: hasNote ? 'left' : 'center',
                                                fontSize: '13px',
                                                wordBreak: 'break-word',
                                                color: currentStatus === 'Từ chối' ? '#e74c3c' :
                                                    currentStatus === 'Yêu cầu chỉnh sửa' ? '#9b59b6' : '#555'
                                            }}>
                                                {hasNote ? item.Note : '—'}
                                            </td>

                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{
                                                    ...getStatusStyle(currentStatus),
                                                    padding: '4px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-block'
                                                }}>
                                                    {currentStatus}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <Button
                                                    icon="fa-solid fa-ellipsis-vertical"
                                                    className="p-button-rounded p-button-text p-button-plain"
                                                    style={{ color: '#6c757d', width: '2.5rem', height: '2.5rem', padding: 0 }}
                                                    onClick={(event) => {
                                                        setSelectedItem(item);
                                                        menu.current.toggle(event);
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {data.length > rows && (
                            <div style={{ marginTop: '15px', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                                <Paginator
                                    first={first}
                                    rows={rows}
                                    totalRecords={data.length}
                                    onPageChange={onPageChange}
                                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                    style={{ background: 'transparent', border: 'none' }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <Menu model={getMenuItems()} popup ref={menu} id="popup_menu" appendTo={document.body} style={{ width: '220px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
        </>
    );
};

export default HourReductionDeclarationListing;